import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CustomerAuthProvider, useCustomerAuth } from "@/lib/customerAuth";
import { Layout } from "@/components/Layout";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Fleet from "@/pages/Fleet";
import Booking from "@/pages/Booking";
import Contact from "@/pages/Contact";
import Devis from "@/pages/Devis";
import PaymentSuccess from "@/pages/PaymentSuccess";
import { MentionsLegales, CGV, CGU } from "@/pages/Legal";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import MemberAuth from "@/pages/MemberAuth";
import MemberDashboard from "@/pages/MemberDashboard";

const Loader = () => (
  <div className="min-h-screen bg-ink flex items-center justify-center text-gold">Chargement…</div>
);

function ProtectedRoute({ children }) {
  const { admin, ready } = useAuth();
  if (!ready) return <Loader />;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

function MemberRoute({ children }) {
  const { customer, ready } = useCustomerAuth();
  if (!ready) return <Loader />;
  if (!customer) return <Navigate to="/membre/connexion" replace />;
  return children;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const { completeGoogle } = useCustomerAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const m = window.location.hash.match(/session_id=([^&]+)/);
    const sid = m ? decodeURIComponent(m[1]) : null;
    (async () => {
      if (sid) { try { await completeGoogle(sid); } catch (e) { /* ignore */ } }
      window.history.replaceState(null, "", window.location.pathname);
      navigate("/membre", { replace: true });
    })();
  }, []);
  return <Loader />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/flotte" element={<Fleet />} />
        <Route path="/reservation" element={<Booking />} />
        <Route path="/devis" element={<Devis />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/cgv" element={<CGV />} />
        <Route path="/cgu" element={<CGU />} />
        <Route path="/membre/connexion" element={<MemberAuth />} />
        <Route path="/membre" element={<MemberRoute><MemberDashboard /></MemberRoute>} />
      </Route>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <CustomerAuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-center" theme="dark" richColors />
      </CustomerAuthProvider>
    </AuthProvider>
  );
}

export default App;
